/* PROTEAM cart-page pairing: same cart and checkout, no independent cart state. */
(() => {
  if (window.__pdhCartPageSyncInstalled) return;
  window.__pdhCartPageSyncInstalled = true;

  const page = () => document.querySelector('cart-items.cart-page-drawer-style');
  const busy = new WeakSet();

  function parse(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function updateFromServer(sections) {
    const cartPage = page();
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (!cartPage || !items || !footer || !sections) return false;

    const itemsSection = sections[items.dataset.id];
    const footerSection = sections[footer.dataset.id];
    const newItems = parse(itemsSection, '#main-cart-items .js-contents');
    const newFooter = parse(footerSection, '#main-cart-footer .js-contents');
    if (!newItems || !newFooter) return false;
    const destination = items.querySelector('.js-contents');
    const footerDestination = footer.querySelector('.js-contents');
    if (!destination || !footerDestination) return false;

    destination.innerHTML = newItems.innerHTML;
    footerDestination.innerHTML = newFooter.innerHTML;
    const itemsWrapper = parse(itemsSection, 'cart-items');
    const footerWrapper = parse(footerSection, '#main-cart-footer');
    if (itemsWrapper) cartPage.classList.toggle('is-empty', itemsWrapper.classList.contains('is-empty'));
    if (footerWrapper) footer.classList.toggle('is-empty', footerWrapper.classList.contains('is-empty'));

    const icon = parse(sections['cart-icon-bubble'], '#cart-icon-bubble');
    const currentIcon = document.getElementById('cart-icon-bubble');
    if (icon && currentIcon) currentIcon.replaceWith(icon);

    const drawerRoot = document.querySelector('cart-drawer');
    const newDrawer = parse(sections['cart-drawer'], '#CartDrawer');
    const currentDrawer = drawerRoot?.querySelector('#CartDrawer');
    if (newDrawer && currentDrawer) {
      const target = currentDrawer.querySelector('.drawer__inner');
      const incoming = newDrawer.querySelector('.drawer__inner');
      if (target && incoming) {
        target.innerHTML = incoming.innerHTML;
        drawerRoot.classList.remove('is-empty');
        drawerRoot.querySelector('cart-drawer-items')?.classList.remove('is-empty');
      }
    }
    return true;
  }

  async function recoverSections(names) {
    const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    url.searchParams.set('sections', names.join(','));
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Cart sections could not be refreshed');
    return response.json();
  }

  document.addEventListener('click', async (event) => {
    const button = event.target?.closest?.('#main-cart-items [data-pdh-cart-addon-add], #main-cart-items [data-pdh-cart-addon-add-both]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || busy.has(button)) return;
    const variantId = button.dataset.pdhVariantId;
    const variantIds = button.dataset.pdhVariantIds ? button.dataset.pdhVariantIds.split(',').filter(Boolean) : [variantId];
    if (!variantId || !variantIds.length || variantIds.some(id => !/^\d+$/.test(id))) return;

    const cartPage = page();
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (!cartPage || !items?.dataset.id || !footer?.dataset.id) return;

    const sections = [items.dataset.id, footer.dataset.id, 'cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
    const status = button.closest('[data-pdh-cart-addon]')?.querySelector('.pdh-cart-addon__status');
    const original = button.innerHTML;
    const loadingText = button.dataset.pdhLoading || 'Adding';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let committed = false;
    busy.add(button);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = loadingText;
    if (status) { status.hidden = true; status.textContent = ''; }
    try {
      let body;
      const headers = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (variantIds.length > 1) {
        // Shopify Cart API: all-or-nothing add; preserve bundled section rendering.
        body = JSON.stringify({
          items: variantIds.map(id => ({ id: Number(id), quantity: 1 })),
          sections: sections.join(','),
          sections_url: window.location.pathname + window.location.search
        });
        headers['Content-Type'] = 'application/json';
      } else {
        const form = new FormData();
        form.set('id', variantId);
        form.set('quantity', '1');
        form.set('sections', sections.join(','));
        form.set('sections_url', window.location.pathname + window.location.search);
        body = form;
      }
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST', body, credentials: 'same-origin', headers, signal: controller.signal
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add this product');
      }
      committed = true;
      let serverSections = result.sections || {};
      if (!serverSections[items.dataset.id] || !serverSections[footer.dataset.id] ||
          !serverSections['cart-icon-bubble'] ||
          (sections.includes('cart-drawer') && !serverSections['cart-drawer'])) {
        // Shopify already added the item. Never POST /cart/add again during recovery.
        serverSections = { ...serverSections, ...(await recoverSections(sections)) };
      }
      if (!updateFromServer(serverSections)) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'pdh-cart-page-addon', productVariantId: variantId, cartData: result
        });
      }
    } catch (error) {
      if (committed) {
        // Cart add succeeded but the UI did not update: reload the authoritative cart.
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }
      if (status?.isConnected) {
        status.hidden = false;
        status.textContent = controller.signal.aborted
          ? (button.dataset.pdhSlow || 'Response delayed; check your cart before retrying.')
          : (button.dataset.pdhError || 'Could not add. Try again.');
      }
      console.error('PROTEAM cart page add-on:', error);
    } finally {
      clearTimeout(timeout);
      busy.delete(button);
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = original;
      }
    }
  });
})();
